import SVG from "../../../Components/SVG";
import { Tooltip } from "react-tooltip";
import { isoToJalali } from "../../../helper/isoToJalali";
import Spinner from "../../../Components/Loader/Spinner";
import {
  FIXED_LEVERAGE_MODE,
  formatLeverage,
  normalizeAllowedLeverages,
} from "../../../helper/leverage";

const present = (value) =>
  value === null || value === undefined || value === "" ? "-" : value;

const InfoRow = ({ label, children }) => (
  <div className="flex gap-2">
    <span className="text-base font-normal text-zi-text-light dark:text-zi-text-dark">
      {label}:
    </span>
    <span className="text-base font-medium text-zi-text-light dark:text-zi-text-dark">
      {children}
    </span>
  </div>
);

export default function AccountInfo({ accountData, accLoading }) {
  const account = accountData?.account ?? {};
  const duration = Number(
    accountData?.trading_terms?.duration_days ?? accountData?.phase?.duration,
  );
  const endDate = (() => {
    if (accountData?.phase?.phase_type === "funded") return "-";
    const source =
      account.status === "live" ? account.created_at : account.updated_at;
    if (!source) return "-";
    if (account.status !== "live") return isoToJalali(source);
    if (!Number.isFinite(duration) || duration <= 0) return "-";
    const date = new Date(source);
    if (Number.isNaN(date.getTime())) return "-";
    date.setDate(date.getDate() + duration);
    return isoToJalali(date.toISOString());
  })();
  const leverageMode =
    account.leverage_mode ?? accountData?.trading_terms?.leverage_mode;
  const fixedLeverage =
    account.leverage ?? accountData?.trading_terms?.fixed_leverage;
  const allowedLeverages = normalizeAllowedLeverages(
    account.allowed_leverages ?? accountData?.trading_terms?.allowed_leverages,
  );
  const leverageTooltipId = "account-variable-leverages-tooltip";

  return (
    <div className="w-full h-fit flex flex-col p-5 gap-5 rounded-2xl bg-zi-bg2-light dark:bg-zi-bg2-dark overflow-hidden">
      <h2 className="flex gap-2.5 items-center">
        <SVG
          name="info"
          classname="size-5 stroke-zi-blue-dark dark:stroke-white"
        />
        <span className="text-lg font-medium text-zi-text-light dark:text-zi-text-dark">
          اطلاعات حساب
        </span>
      </h2>
      {!accLoading ? (
        <div className="w-full h-fit text-nowrap overflow-auto flex divide-x divide-x-reverse divide-zi-border-light dark:divide-zi-border-dark [&>*:not(:last-child)]:pl-5 [&>*:not(:first-child)]:pr-5">
          <div className="w-full flex flex-col gap-5">
            <InfoRow label="سرمایه اولیه">
              {account.initial_balance == null
                ? "-"
                : `${Number(account.initial_balance).toLocaleString()} دلار`}
            </InfoRow>
            <InfoRow label="نوع بازار">ارز دیجیتال</InfoRow>
            {leverageMode === FIXED_LEVERAGE_MODE ? (
              <InfoRow label="اهرم">
                <bdi dir="ltr">{formatLeverage(fixedLeverage)}</bdi>
              </InfoRow>
            ) : (
              <div dir="rtl" className="flex items-center gap-2">
                <span className="text-base font-normal text-zi-text-light dark:text-zi-text-dark">
                  اهرم:
                </span>
                <span className="text-base font-medium text-zi-text-light dark:text-zi-text-dark">
                  متغیر
                </span>
                <button
                  type="button"
                  data-tooltip-id={leverageTooltipId}
                  aria-label="نمایش اهرم‌های مجاز"
                  className="flex items-center gap-1.5 bg-zi-inputbg-dark px-2 py-1 rounded-md"
                >
                  <SVG
                    name="infoCircle"
                    classname="stroke-zi-text-light fill-zi-text-light dark:stroke-zi-text-dark dark:fill-zi-text-dark"
                  />
                </button>
                <Tooltip
                  id={leverageTooltipId}
                  place="bottom"
                  opacity={1}
                  openEvents={{ mouseover: true, focus: true, click: true }}
                  closeEvents={{ mouseout: true, blur: true }}
                  globalCloseEvents={{ clickOutsideAnchor: true, escape: true }}
                  className="z-50 !w-64 !max-w-[calc(100vw-2rem)] !p-3 !bg-zi-blue-dark !rounded-lg"
                  render={() => (
                    <div dir="rtl" className="flex flex-col gap-2">
                      <span className="text-sm font-medium">اهرم‌های مجاز</span>
                      <div className="grid grid-cols-3 gap-1.5" dir="ltr">
                        {allowedLeverages.length ? (
                          allowedLeverages.map((value) => (
                            <span
                              key={value}
                              className="rounded-md bg-white/10 px-2 py-1 text-center text-xs font-light"
                            >
                              {formatLeverage(value)}
                            </span>
                          ))
                        ) : (
                          <span className="col-span-3 text-center text-xs font-light">
                            -
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                />
              </div>
            )}
          </div>
          <div className="w-full flex flex-col gap-5">
            <InfoRow label="نوع حساب">
              {accountData?.challenge?.challenge_type === "real"
                ? "چالش"
                : accountData?.challenge
                  ? "تمرینی"
                  : "-"}
            </InfoRow>
            <InfoRow label="نوع چالش">
              {accountData?.challenge
                ? `${present(accountData.challenge.name)} | ${present(accountData.challenge.category)}`
                : "-"}
            </InfoRow>
            <InfoRow label="نوع فاز">
              {accountData?.phase?.phase_type === "phase1"
                ? "فاز اول"
                : accountData?.phase?.phase_type === "phase2"
                  ? "فاز دوم"
                  : accountData?.phase?.phase_type === "funded"
                    ? "فاندد"
                    : "-"}
            </InfoRow>
          </div>
          <div className="w-full flex flex-col gap-5">
            <InfoRow label="تاریخ شروع">
              {account.created_at ? isoToJalali(account.created_at) : "-"}
            </InfoRow>
            <InfoRow label="تاریخ پایان">{endDate}</InfoRow>
            <InfoRow label="پلتفرم معامله">پلتفرم کوژیتو</InfoRow>
          </div>
        </div>
      ) : (
        <div className="w-full h-[112px] flex justify-center items-center">
          <Spinner color="#8080804D" />
        </div>
      )}
    </div>
  );
}
